import { useCallback, useEffect, useRef, useState } from "react"
import { DIALOG_CLOSE_HINT_MS, DIALOG_NUDGE_ANIMATION_MS } from "@/shared/lib/dialog/model/const"

const NUDGE_KEYFRAMES: Keyframe[] = [0, -8, 8, -5, 5, 0].map((offsetPx) => ({
  transform: `translateX(${offsetPx}px)`,
  easing: "ease-in-out",
}))

// Controlled state for the close button tooltip, which every dialog shows on hover, plus the
// feedback for dismiss attempts that a persistent dialog refuses: the dialog nudges from side
// to side and the same tooltip pops open to point at the close button.
export function useDialogCloseHint() {
  const [isCloseHintOpen, setIsCloseHintOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const nudgeRef = useRef<Animation | null>(null)
  const closeHintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      nudgeRef.current?.cancel()

      if (closeHintTimeoutRef.current !== null) {
        clearTimeout(closeHintTimeoutRef.current)
      }
    }
  }, [])

  const triggerHint = useCallback(() => {
    const content = contentRef.current

    if (content) {
      // Cancelling first is what restarts the nudge when the user keeps hammering Escape.
      nudgeRef.current?.cancel()
      nudgeRef.current = content.animate(NUDGE_KEYFRAMES, DIALOG_NUDGE_ANIMATION_MS)
    }

    setIsCloseHintOpen(true)

    if (closeHintTimeoutRef.current !== null) {
      clearTimeout(closeHintTimeoutRef.current)
    }

    closeHintTimeoutRef.current = setTimeout(() => setIsCloseHintOpen(false), DIALOG_CLOSE_HINT_MS)
  }, [])

  return { contentRef, isCloseHintOpen, setIsCloseHintOpen, triggerHint }
}
